import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder'
import FolderIcon from '@mui/icons-material/Folder'
import WarningIcon from '@mui/icons-material/Warning'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import {
  useClickUpSpaces,
  useClickUpFolders,
  useClickUpLists,
  useClickUpFields,
  useCreateClickUpFolder,
  useCreateClickUpList,
  useDeployStrategy,
  useUpdateStrategy,
} from 'api/aws/strategy-builder/useStrategyBuilder'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useBuilderContext } from './StrategyBuilderContext'
import { DeployTarget } from './types'

interface Props {
  open: boolean
  onClose: () => void
}

interface DeployResult {
  totalDeployed: number
  totalFailed: number
  spaceName: string
  folderName: string
  listName: string
  items: Array<{ title: string; points: number; sku: string }>
  totalPoints: number
  totalValue: number
}

const StrategyDeployDialog = ({ open, onClose }: Props) => {
  const { state, dispatch } = useBuilderContext()
  const { showSnackbar } = useSnackbarContext()
  const navigate = useNavigate()
  const deployMutation = useDeployStrategy()
  const updateStrategyMutation = useUpdateStrategy()
  const createFolderMutation = useCreateClickUpFolder()
  const createListMutation = useCreateClickUpList()

  const [deployResult, setDeployResult] = useState<DeployResult | null>(null)
  const [selectedSpace, setSelectedSpace] = useState<string | null>(null)
  const [deployMode, setDeployMode] = useState<'new' | 'existing'>('new')

  // Existing hierarchy selection
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null)
  const [selectedList, setSelectedList] = useState<string | null>(null)

  // New hierarchy creation
  const [newFolderName, setNewFolderName] = useState('')
  const [newListName, setNewListName] = useState('')

  // Auto-match state
  const [autoMatchedFolder, setAutoMatchedFolder] = useState<string | null>(null)
  const [autoMatchedList, setAutoMatchedList] = useState<string | null>(null)

  // Custom field mappings
  const [skuFieldId, setSkuFieldId] = useState('')
  const [budgetMonthFieldId, setBudgetMonthFieldId] = useState('')

  const spacesQuery = useClickUpSpaces()
  const foldersQuery = useClickUpFolders(selectedSpace)
  const listsQuery = useClickUpLists(deployMode === 'existing' ? selectedFolder : autoMatchedFolder)
  const fieldsQuery = useClickUpFields(selectedList)

  const spaces = spacesQuery.data ?? []
  const folders = foldersQuery.data ?? []
  const lists = listsQuery.data ?? []
  const fields = fieldsQuery.data ?? []

  const clientName = state.strategy?.clientName ?? ''

  // Auto-populate folder/list names from client name
  useEffect(() => {
    if (clientName && deployMode === 'new') {
      setNewFolderName(clientName)
      setNewListName(state.strategy?.name ?? `${clientName} Strategy`)
    }
  }, [clientName, deployMode, state.strategy?.name])

  // Auto-match: check if any existing folder matches the client name
  useEffect(() => {
    if (!clientName || !folders.length) {
      setAutoMatchedFolder(null)
      setAutoMatchedList(null)
      return
    }
    const normalizedClient = clientName.toLowerCase().trim()
    const match = folders.find(f => f.name.toLowerCase().trim() === normalizedClient)
    if (match) {
      setAutoMatchedFolder(match.id)
    } else {
      setAutoMatchedFolder(null)
      setAutoMatchedList(null)
    }
  }, [clientName, folders])

  // Auto-match: check lists within matched folder
  useEffect(() => {
    if (!autoMatchedFolder || deployMode !== 'new' || !lists.length) {
      setAutoMatchedList(null)
      return
    }
    const normalizedList = newListName.toLowerCase().trim()
    const match = lists.find(l => l.name.toLowerCase().trim() === normalizedList)
    setAutoMatchedList(match?.id ?? null)
  }, [autoMatchedFolder, deployMode, lists, newListName])

  const spaceName = spaces.find(s => s.id === selectedSpace)?.name ?? ''
  const folderName = deployMode === 'existing'
    ? folders.find(f => f.id === selectedFolder)?.name ?? ''
    : newFolderName
  const listName = deployMode === 'existing'
    ? lists.find(l => l.id === selectedList)?.name ?? ''
    : newListName

  const canDeploy = useMemo(() => {
    if (state.selectedItems.length === 0) return false
    if (deployMode === 'existing') return !!selectedList
    return !!selectedSpace && newFolderName.trim().length > 0 && newListName.trim().length > 0
  }, [state.selectedItems.length, deployMode, selectedList, selectedSpace, newFolderName, newListName])

  const handleOpenSpaces = useCallback(() => {
    spacesQuery.refetch()
  }, [spacesQuery])

  const handleDeploy = useCallback(async () => {
    if (!state.strategyId) return

    let targetListId = selectedList
    let targetFolderName = folderName
    let targetListName = listName

    // If creating new hierarchy, create folder + list first
    if (deployMode === 'new' && selectedSpace) {
      try {
        dispatch({ type: 'SET_DEPLOYING', payload: true })

        // Check if folder already exists (auto-matched)
        let folderId = autoMatchedFolder
        if (!folderId) {
          const folder = await createFolderMutation.mutateAsync({
            spaceId: selectedSpace,
            name: newFolderName.trim(),
          })
          folderId = folder.id
          targetFolderName = folder.name
        } else {
          targetFolderName = folders.find(f => f.id === folderId)?.name ?? newFolderName
        }

        // Use existing list if auto-matched, otherwise create new
        if (autoMatchedList) {
          targetListId = autoMatchedList
          targetListName = lists.find(l => l.id === autoMatchedList)?.name ?? newListName
        } else {
          const list = await createListMutation.mutateAsync({
            folderId,
            name: newListName.trim(),
          })
          targetListId = list.id
          targetListName = list.name
        }
      } catch (err) {
        showSnackbar('Failed to create ClickUp folder/list', 'error')
        dispatch({ type: 'SET_DEPLOYING', payload: false })
        return
      }
    }

    if (!targetListId) {
      showSnackbar('No target list selected', 'error')
      return
    }

    const target: DeployTarget = {
      listId: targetListId,
      listName: targetListName,
      spaceName,
      folderName: targetFolderName,
      serviceLine: '',
      month: '',
      customFieldMappings: {
        ...(skuFieldId && { sku: skuFieldId }),
        ...(budgetMonthFieldId && { budgetMonth: budgetMonthFieldId }),
      },
    }

    if (deployMode !== 'new') dispatch({ type: 'SET_DEPLOYING', payload: true })
    try {
      const result = await deployMutation.mutateAsync({
        strategyId: state.strategyId,
        targets: [target],
      })

      const deployments = result.deployments ?? []
      const totalDeployed = deployments.reduce((sum: number, d: any) => sum + (d.itemsDeployed ?? 0), 0)
      const totalFailed = deployments.reduce((sum: number, d: any) => sum + (d.itemsFailed ?? 0), 0)

      // Build deployed items summary from selected items
      // selectedItems may be string IDs or full objects depending on context
      const deployedItems = state.selectedItems.map((entry: any) => {
        const itemId = typeof entry === 'string' ? entry : (entry?.PK ?? entry?.sku ?? entry?.itemId ?? '')
        const itemTitle = typeof entry === 'string' ? undefined : (entry?.taskName ?? entry?.title)
        const itemPoints = typeof entry === 'string' ? undefined : Number(entry?.points ?? 0)
        const itemSku = typeof entry === 'string' ? undefined : (entry?.sku ?? '')

        // Try to find in catalog for more info
        const catalogItem = state.catalog.find(c => c.PK === itemId || c.sku === itemId)
        return {
          title: itemTitle ?? catalogItem?.title ?? String(itemId),
          points: itemPoints ?? Number(catalogItem?.points ?? 0),
          sku: itemSku ?? catalogItem?.sku ?? '',
        }
      })
      const totalPoints = deployedItems.reduce((sum, i) => sum + i.points, 0)
      const totalValue = state.selectedItems.reduce((sum: number, entry: any) => {
        const itemId = typeof entry === 'string' ? entry : (entry?.PK ?? entry?.sku ?? '')
        const entryPrice = typeof entry === 'string' ? undefined : Number(entry?.marketValue ?? entry?.price ?? 0)
        const catalogItem = state.catalog.find(c => c.PK === itemId || c.sku === itemId)
        return sum + (entryPrice ?? Number(catalogItem?.price ?? 0))
      }, 0)

      // Mark strategy as deployed in META record
      if (state.strategyId) {
        await updateStrategyMutation.mutateAsync({
          id: state.strategyId,
          status: 'deployed',
          totalPoints,
          totalMarketValue: totalValue,
          itemCount: deployedItems.length,
          deployedAt: new Date().toISOString(),
        }).catch(() => {}) // Non-critical — don't block deploy result
      }

      setDeployResult({
        totalDeployed,
        totalFailed,
        spaceName,
        folderName: targetFolderName,
        listName: targetListName,
        items: deployedItems,
        totalPoints,
        totalValue,
      })
    } catch {
      showSnackbar('Failed to deploy to ClickUp', 'error')
    } finally {
      dispatch({ type: 'SET_DEPLOYING', payload: false })
    }
  }, [
    state.strategyId, selectedList, selectedSpace, deployMode, autoMatchedFolder, autoMatchedList,
    newFolderName, newListName, folderName, listName, spaceName, skuFieldId,
    budgetMonthFieldId, folders, lists, createFolderMutation, createListMutation,
    deployMutation, dispatch, showSnackbar, state.selectedItems, state.catalog,
  ])

  const matchedFolderName = autoMatchedFolder
    ? folders.find(f => f.id === autoMatchedFolder)?.name
    : null
  const matchedListName = autoMatchedList
    ? lists.find(l => l.id === autoMatchedList)?.name
    : null

  const handleClose = () => {
    setDeployResult(null)
    onClose()
  }

  // ── Deployment Summary Screen ──────────────────────────────────────────────
  if (deployResult) {
    const hasFailures = deployResult.totalFailed > 0
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasFailures ? (
            <WarningIcon color="warning" />
          ) : (
            <CheckCircleIcon color="success" />
          )}
          {hasFailures ? 'Deployment Completed with Issues' : 'Strategy Deployed Successfully'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {/* Summary stats */}
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', py: 1 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {deployResult.totalDeployed}
                </Typography>
                <Typography variant="caption" color="text.secondary">Tasks Deployed</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {deployResult.totalPoints}
                </Typography>
                <Typography variant="caption" color="text.secondary">Total Points</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  ${deployResult.totalValue.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">MV Value</Typography>
              </Box>
              {hasFailures && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {deployResult.totalFailed}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Failed</Typography>
                </Box>
              )}
            </Box>

            <Divider />

            {/* ClickUp location */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Deployed To
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {deployResult.spaceName} → {deployResult.folderName} → {deployResult.listName}
              </Typography>
            </Box>

            {/* Client info */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Client
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {state.strategy?.clientName ?? 'N/A'}
                {state.strategy?.months?.length ? ` — ${state.strategy.months.join(', ')}` : ''}
              </Typography>
            </Box>

            <Divider />

            {/* Deployed items list */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Items Deployed ({deployResult.items.length})
              </Typography>
              <Box sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
                {deployResult.items.map((item, idx) => (
                  <Box
                    key={`${item.sku}-${idx}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      '&:nth-of-type(even)': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, mr: 1 }}>
                      {item.title}
                    </Typography>
                    <Chip label={`${item.points} pts`} size="small" sx={{ minWidth: 55 }} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setDeployResult(null)
            }}
          >
            Deploy More Items
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              handleClose()
              navigate('/organizations')
            }}
          >
            View Organizations
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleClose}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  // ── Deploy Configuration Screen ────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Deploy to ClickUp</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Deploy {state.selectedItems.length} strategy items as tasks in ClickUp.
          </Typography>

          {/* Load spaces button */}
          {spaces.length === 0 && (
            <Button variant="outlined" onClick={handleOpenSpaces} disabled={spacesQuery.isFetching}>
              {spacesQuery.isFetching ? 'Loading...' : 'Load ClickUp Workspaces'}
            </Button>
          )}

          {/* Space selector */}
          {spaces.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Space</InputLabel>
              <Select
                value={selectedSpace ?? ''}
                label="Space"
                onChange={e => {
                  setSelectedSpace(e.target.value)
                  setSelectedFolder(null)
                  setSelectedList(null)
                  setAutoMatchedFolder(null)
                }}
              >
                {spaces.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Deploy mode toggle */}
          {selectedSpace && (
            <>
              <ToggleButtonGroup
                value={deployMode}
                exclusive
                onChange={(_, v) => { if (v) setDeployMode(v) }}
                size="small"
                fullWidth
              >
                <ToggleButton value="new">
                  <CreateNewFolderIcon sx={{ mr: 1, fontSize: 18 }} />
                  Create New Hierarchy
                </ToggleButton>
                <ToggleButton value="existing">
                  <FolderIcon sx={{ mr: 1, fontSize: 18 }} />
                  Use Existing
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Auto-match alert */}
              {deployMode === 'new' && matchedFolderName && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  A folder named <strong>"{matchedFolderName}"</strong> already exists in this space.
                  Tasks will be added as a new list inside the existing folder.
                </Alert>
              )}
            </>
          )}

          {/* NEW HIERARCHY MODE */}
          {selectedSpace && deployMode === 'new' && (
            <>
              <TextField
                label="Folder Name"
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                fullWidth
                size="small"
                helperText={matchedFolderName
                  ? 'Existing folder will be used — no duplicate created'
                  : 'A new folder will be created in the selected space'}
                color={matchedFolderName ? 'info' : undefined}
              />
              <TextField
                label="List Name"
                value={newListName}
                onChange={e => setNewListName(e.target.value)}
                fullWidth
                size="small"
                helperText={matchedListName
                  ? 'Existing list will be used — tasks will be added to it'
                  : 'A new list will be created inside the folder'}
                color={matchedListName ? 'info' : undefined}
              />
              {matchedListName && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  A list named <strong>"{matchedListName}"</strong> already exists in this folder.
                  Tasks will be deployed into the existing list instead of creating a duplicate.
                </Alert>
              )}
            </>
          )}

          {/* EXISTING HIERARCHY MODE — checkbox-based selection */}
          {selectedSpace && deployMode === 'existing' && (
            <>
              {/* Folder selection with checkboxes */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                  Select Folder
                </Typography>
                {foldersQuery.isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Loading folders...</Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', bgcolor: 'background.default', borderRadius: 1, p: 1 }}>
                    {folders.map(f => {
                      const isMatch = clientName && f.name.toLowerCase().trim() === clientName.toLowerCase().trim()
                      return (
                        <Box
                          key={f.id}
                          onClick={() => {
                            setSelectedFolder(f.id)
                            setSelectedList(null)
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.5,
                            px: 1,
                            borderRadius: 1,
                            cursor: 'pointer',
                            bgcolor: selectedFolder === f.id ? 'action.selected' : 'transparent',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Checkbox checked={selectedFolder === f.id} size="small" sx={{ p: 0.25 }} />
                          <FolderIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ flex: 1 }}>{f.name}</Typography>
                          {isMatch && (
                            <Chip label="Client match" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                )}
              </Box>

              {/* Load Lists button */}
              {selectedFolder && !listsQuery.data && (
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  disabled={listsQuery.isLoading}
                  startIcon={listsQuery.isLoading ? <CircularProgress size={14} /> : undefined}
                  sx={{ textTransform: 'none' }}
                >
                  {listsQuery.isLoading ? 'Loading Lists...' : 'Loading Lists...'}
                </Button>
              )}

              {/* List selection with checkboxes */}
              {selectedFolder && lists.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    Select List
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', bgcolor: 'background.default', borderRadius: 1, p: 1 }}>
                    {lists.map(l => (
                      <Box
                        key={l.id}
                        onClick={() => setSelectedList(l.id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.5,
                          px: 1,
                          borderRadius: 1,
                          cursor: 'pointer',
                          bgcolor: selectedList === l.id ? 'action.selected' : 'transparent',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Checkbox checked={selectedList === l.id} size="small" sx={{ p: 0.25 }} />
                        <Typography variant="body2">{l.name}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Custom field mappings (optional) — show when a list is selected or will be created */}
          {((deployMode === 'existing' && selectedList && fields.length > 0) || false) && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>
                Custom Field Mappings (optional)
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>SKU Field</InputLabel>
                <Select value={skuFieldId} label="SKU Field" onChange={e => setSkuFieldId(e.target.value)}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {fields.map(f => (
                    <MenuItem key={f.id} value={f.id}>{f.name} ({f.type})</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Budget Month Field</InputLabel>
                <Select value={budgetMonthFieldId} label="Budget Month Field" onChange={e => setBudgetMonthFieldId(e.target.value)}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {fields.map(f => (
                    <MenuItem key={f.id} value={f.id}>{f.name} ({f.type})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {/* Preview */}
          {canDeploy && (
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Deployment Preview
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {spaceName} → {deployMode === 'new'
                  ? `${newFolderName}${matchedFolderName ? ' (existing)' : ' (new)'} → ${newListName}${matchedListName ? ' (existing)' : ' (new)'}`
                  : `${folderName} → ${listName}`
                }
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {state.selectedItems.length} tasks will be created
              </Typography>
            </Box>
          )}

          {state.isDeploying && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">
                {createFolderMutation.isPending ? 'Creating folder...'
                  : createListMutation.isPending ? 'Creating list...'
                  : 'Deploying tasks to ClickUp...'}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={state.isDeploying}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleDeploy}
          disabled={!canDeploy || state.isDeploying}
        >
          {state.isDeploying ? 'Deploying...' : `Deploy ${state.selectedItems.length} Items`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default StrategyDeployDialog
